export const RECAPTCHA_FIELD = 'g-recaptcha-response'

interface IGrecaptcha {
	ready: (cb: () => void) => void
	execute: (siteKey: string, options: { action: string }) => Promise<string>
}

let scriptPromise: Promise<IGrecaptcha> | null = null

const loadRecaptcha = (siteKey: string) => {
	if (scriptPromise) return scriptPromise

	scriptPromise = new Promise<IGrecaptcha>((resolve, reject) => {
		const script = document.createElement('script')
		script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
		script.async = true
		script.setAttribute('data-cookieconsent', 'ignore')
		script.addEventListener('load', () => {
			const grecaptcha = (window as any).grecaptcha as IGrecaptcha | undefined
			if (!grecaptcha) return reject(new Error('reCAPTCHA script loaded without grecaptcha'))
			grecaptcha.ready(() => resolve(grecaptcha))
		})
		script.addEventListener('error', () => reject(new Error('reCAPTCHA script failed to load')))
		document.head.appendChild(script)
	}).catch((error) => {
		scriptPromise = null
		throw error
	})

	return scriptPromise
}

export const useRecaptchaService = () => {
	const siteKey = useRuntimeConfig().public.recaptchaSiteKey as string | undefined

	const getToken = async (action: string): Promise<string | undefined> => {
		if (!import.meta.client || !siteKey) return undefined

		try {
			const grecaptcha = await loadRecaptcha(siteKey)
			return await grecaptcha.execute(siteKey, { action })
		} catch (error) {
			logger.error(error)
			return undefined
		}
	}

	const withToken = async <T extends object>(body: T, action: string) => {
		const token = await getToken(action)
		return token ? { ...body, [RECAPTCHA_FIELD]: token } : body
	}

	return { getToken, withToken }
}
